import { db } from '../db/dexie';
import { opLogService } from '../db/op-log.service';
import api from './auth.service';

/**
 * Serviço de Sincronização
 * AIDEV-NOTE: Sincroniza Op-Log com o servidor
 */

// AIDEV-NOTE: Converte o snapshot lossy do Dexie (db.pedidos.itens) de volta para AddItemDto.
// referencia cai para o proprio codigo do produto — o snapshot nao guarda referencia/cor/tamanho,
// mas um pedido com os itens certos e quantidades certas vale muito mais do que um pedido vazio.
async function itensDoSnapshotLocal(pedidoId: string): Promise<unknown[]> {
  const local = await db.pedidos.get(pedidoId);
  if (!local?.itens?.length) return [];

  return local.itens.map((item) => ({
    produto_codigo: item.produto_id,
    referencia: String(item.produto_id),
    descricao: item.descricao,
    quantidade: item.quantidade,
    valor_unitario: item.preco_unitario,
    desconto_percentual: item.desconto ?? 0
  }));
}

export const syncService = {
  /**
   * Sincroniza operações pendentes
   */
  async syncPendingOperations(): Promise<{
    synced: number;
    failed: number;
  }> {
    // AIDEV-NOTE: Itens de rascunho digitados online com PATCH pendente NAO viram op propria:
    // o carrinho persiste no localStorage (pedido.store) e a AutosaveQueue reenvia ao reabrir
    // o wizard (Task recuperacao). Op SAVE_ITENS separada criaria corrida com o replace-all.
    // AIDEV-NOTE: antes de cada drain, resgata ops orfas presas em 'syncing' (app fechado com o
    // request em voo). Sem isto elas nunca mais entram num batch — pedido perdido em silencio.
    await opLogService.recuperarOpsTravadas();

    const pendingOps = await opLogService.getPendingOperations(50);

    let synced = 0;
    let failed = 0;

    for (const op of pendingOps) {
      try {
        const claimed = await opLogService.markAsSyncing(op.id);
        if (!claimed) {
          // AIDEV-NOTE: op cancelada (ex.: exclusao de rascunho) entre o fetch do batch e o processamento
          continue;
        }

        // Descriptografa payload
        const payload = await opLogService.decrypt(op.encrypted_payload);

        // Envia para o servidor baseado no tipo de operação
        switch (op.operation_type) {
          case 'CREATE_PEDIDO':
            await this.syncCreatePedido(op, payload);
            break;
          case 'FINALIZAR_PEDIDO':
            await this.syncFinalizarPedido(op, payload);
            break;
          case 'DELETE_PEDIDO':
            await this.deletePedido(op, payload);
            break;
          case 'CREATE_VISITA':
          case 'UPDATE_VISITA':
            await this.syncVisita(op, payload);
            break;
          case 'CREATE_LEAD':
          case 'UPDATE_LEAD':
            await this.syncLead(op, payload);
            break;
          default:
            console.warn('Operação desconhecida:', op.operation_type);
        }

        await opLogService.markAsSynced(op.id);
        synced++;
      } catch (error) {
        console.error(`Erro ao sincronizar operação ${op.id}:`, error);
        await opLogService.markAsFailed(
          op.id,
          error instanceof Error ? error.message : String(error)
        );
        failed++;
      }
    }

    return { synced, failed };
  },

  /**
   * Cria pedido no servidor (drain do op_log)
   * AIDEV-NOTE: payload contem o id offline — server aceita e mantem o mesmo UUID (create idempotente).
   * PATCH de itens em seguida restaura os itens do rascunho (o Zod do POST descarta campos extras).
   */
  async syncCreatePedido(_op: { id: string }, payload: Record<string, unknown>): Promise<void> {
    const pedidoId = payload.id as string;

    const response = await api.post('/pedidos', payload);
    const criado = (response.data as { pedido?: { status?: string } } | undefined)?.pedido;

    // AIDEV-NOTE: Defesa em profundidade — a op CREATE_PEDIDO nasce com itens: [] (o rascunho e
    // criado logo apos escolher o cliente). O autosave mescla os itens no payload enquanto o
    // wizard esta aberto; se o app foi fechado antes disso, caimos no snapshot local do Dexie.
    // Sem este resgate o drain criava um pedido VAZIO no servidor e marcava synced — perda total
    // do que o vendedor digitou sem conexao (relato de campo 2026-10).
    const itensPayload = payload.itens as unknown[] | undefined;
    const itens =
      itensPayload && itensPayload.length > 0 ? itensPayload : await itensDoSnapshotLocal(pedidoId);
    // AIDEV-NOTE: retry idempotente pode devolver pedido ja finalizado — PATCH de itens so vale em rascunho
    const aindaRascunho = !criado || criado.status === 'rascunho';
    if (itens && itens.length > 0 && aindaRascunho) {
      await api.patch(`/pedidos/${pedidoId}/itens`, { itens });
    }

    await db.pedidos.update(pedidoId, { synced_at: Date.now() });
  },

  /**
   * Finaliza pedido no servidor (drain do op_log)
   * AIDEV-NOTE: payload e o FinalizarPedidoDto completo + id/vendedor_id (descartados pelo Zod do server)
   */
  async syncFinalizarPedido(_op: { id: string }, payload: Record<string, unknown>): Promise<void> {
    const pedidoId = payload.id as string;

    await api.post(`/pedidos/${pedidoId}/finalizar`, payload);

    await db.pedidos.update(pedidoId, { status: 'finalizado', synced_at: Date.now() });
  },

  /**
   * Exclui pedido no servidor (drain do op_log)
   * AIDEV-NOTE: 404 = sucesso (idempotencia — pedido ja nao existe); 400 propaga p/ retry (max 3)
   */
  async deletePedido(_op: { id: string }, payload: Record<string, unknown>): Promise<void> {
    const pedidoId = payload.id as string;

    try {
      await api.delete(`/pedidos/${pedidoId}`);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        return;
      }
      throw error;
    }
  },

  /**
   * Sincroniza visita
   */
  async syncVisita(_op: { id: string }, payload: Record<string, unknown>): Promise<void> {
    await api.post('/visitas', payload);
  },

  /**
   * Sincroniza lead
   */
  async syncLead(_op: { id: string }, payload: Record<string, unknown>): Promise<void> {
    await api.post('/leads', payload);
  },

  /**
   * Busca dados do servidor (quando online)
   */
  async fetchFromServer<T>(endpoint: string): Promise<T> {
    const response = await api.get<T>(endpoint);
    return response.data;
  },

  /**
   * Envia dados para o servidor
   */
  async sendToServer<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await api.post<T>(endpoint, data);
    return response.data;
  }
};
